import React from 'react';
import ShimmerEffect from './ShimmerEffect';

const CaseDetailSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Case Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Section */}
            <ShimmerEffect>
              <div className="card p-6 bg-white">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="h-9 w-64 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-4 w-48 bg-gray-200 rounded mb-4"></div>

                    {/* Subject Tags */}
                    <div className="flex flex-wrap gap-2">
                      <div className="h-8 w-32 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="h-9 w-32 bg-gray-200 rounded mb-1"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </ShimmerEffect>

            {/* Location & Basic Info */}
            <ShimmerEffect>
              <div className="card p-6 bg-white">
                <div className="h-7 w-40 bg-gray-200 rounded mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Location Item */}
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 w-32 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-28 bg-gray-200 rounded"></div>
                    </div>
                  </div>

                  {/* Frequency Item */}
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-5 w-20 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 w-28 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-36 bg-gray-200 rounded"></div>
                    </div>
                  </div>

                  {/* Time Item */}
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-5 w-32 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 w-40 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-24 bg-gray-200 rounded"></div>
                    </div>
                  </div>

                  {/* Duration Item */}
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-5 w-28 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 w-36 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-20 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ShimmerEffect>

            {/* Description Section */}
            <ShimmerEffect>
              <div className="card p-6 bg-white">
                <div className="h-7 w-48 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </ShimmerEffect>

            {/* Requirements Section */}
            <ShimmerEffect>
              <div className="card p-6 bg-white">
                <div className="h-7 w-40 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-full bg-gray-200 rounded"></div>
                  <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
                </div>
              </div>
            </ShimmerEffect>
          </div>

          {/* Right Column - Parent Info */}
          <div className="lg:col-span-1">
            <ShimmerEffect>
              <div className="card p-6 bg-white">
                <div className="h-7 w-40 bg-gray-200 rounded mb-6"></div>

                {/* Parent Avatar and Name */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-5 w-24 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 w-20 bg-gray-200 rounded"></div>
                  </div>
                </div>

                {/* Parent Info Items */}
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-4 w-20 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-32 bg-gray-200 rounded"></div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-gray-200 rounded flex-shrink-0 mt-1"></div>
                    <div className="flex-1">
                      <div className="h-4 w-24 bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-28 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="h-10 w-full bg-gray-200 rounded mt-6"></div>
              </div>
            </ShimmerEffect>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseDetailSkeleton;
